import { useSyncExternalStore } from 'react'
import { cn } from '../cn.ts'

export type ToastTone = 'neutral' | 'success' | 'attention'
interface ToastItem {
  id: number
  text: string
  tone: ToastTone
  action?: { label: string; onClick: () => void }
}

let items: ToastItem[] = []
let seq = 0
const listeners = new Set<() => void>()
const emit = () => {
  for (const l of listeners) l()
}

/** Shows a short, sober notification (§21.1 tonality). Returns a dismiss function. */
export function toast(
  text: string,
  opts: { tone?: ToastTone; action?: ToastItem['action']; ms?: number } = {},
) {
  const id = ++seq
  items = [...items, { id, text, tone: opts.tone ?? 'neutral', action: opts.action }]
  emit()
  const dismiss = () => {
    items = items.filter((t) => t.id !== id)
    emit()
  }
  setTimeout(dismiss, opts.ms ?? 4000)
  return dismiss
}

const subscribe = (l: () => void) => {
  listeners.add(l)
  return () => listeners.delete(l)
}

export function Toaster() {
  const list = useSyncExternalStore(
    subscribe,
    () => items,
    () => items,
  )
  return (
    <div
      aria-live="polite"
      className="pointer-events-none fixed inset-x-0 bottom-20 z-[60] flex flex-col items-center gap-2 px-4 md:bottom-6"
    >
      {list.map((t) => (
        <div
          key={t.id}
          className={cn(
            'pointer-events-auto flex items-center gap-3 rounded-pill border bg-card px-4 py-2 text-[13px] text-text-1 shadow-2',
            t.tone === 'attention'
              ? 'border-apricot'
              : t.tone === 'success'
                ? 'border-green'
                : 'border-line-1',
          )}
        >
          <span>{t.text}</span>
          {t.action && (
            <button
              type="button"
              className="font-medium text-pop-fg"
              onClick={() => {
                t.action?.onClick()
                items = items.filter((x) => x.id !== t.id)
                emit()
              }}
            >
              {t.action.label}
            </button>
          )}
        </div>
      ))}
    </div>
  )
}
